import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, TextField, Box, FormControlLabel, Checkbox, DialogContentText } from '@mui/material';
import { usePlanHintCreateMutation } from '../../../services/planHints';
import { useTranslation } from 'react-i18next';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from 'react-ga4';

interface SuggestionDialogProps {
  open: boolean;
  onClose: () => void;
}

const SuggestionDialog: React.FC<SuggestionDialogProps> = ({ open, onClose }) => {
	const [createPlanHintMutation, {isSuccess}] = usePlanHintCreateMutation();
	const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
	const { t } = useTranslation();

	useEffect(() => {
		if (open) {
			ReactPixel.trackCustom('SuggestionDialogOpened');
			ReactGA.event({
				category: 'UI',
				action: 'SuggestionDialogOpened',
			});
		}
	}, [open]);

	const handleSuccessClose = () => {
		onClose();
		setIsSuccessDialogOpen(false);
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const formData = new FormData(event.target as HTMLFormElement);
		const planHint = {
			type: 'wish',
			title: 'idea',
			description: formData.get('description') as string,
			email: formData.get('email') as string,
			notifyMeConsent: Boolean(formData.get('notifyMeConsent')),
			marketingConsent: Boolean(formData.get('marketingConsent')),
			shareInstructionsConsent: false,
		};
		createPlanHintMutation(planHint);
		onClose();
	};
	const [isMarketingConsentChecked, setIsMarketingConsentChecked] = useState(false);
	const [isNotifyMeConsentChecked, setIsNotifyMeConsentChecked] = useState(false);
	const shouldDisplayEmailField = useMemo(() => {
		return isNotifyMeConsentChecked || isMarketingConsentChecked;
	}, [isNotifyMeConsentChecked, isMarketingConsentChecked]);

	useEffect(() => {
		if (isSuccess) {
			setIsSuccessDialogOpen(true);
		}
	}, [isSuccess]);

	return (<>
		<Dialog open={isSuccessDialogOpen} onClose={handleSuccessClose} maxWidth="sm" fullWidth>
			<DialogTitle>{t('addPlanHintDialog.successTitle')}</DialogTitle>
			<DialogContent>
				<Typography component="p" py={2}>
					{t('addPlanHintDialog.successDescription')}
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleSuccessClose}>{t('addPlanHintDialog.close')}</Button>
			</DialogActions>
		</Dialog>
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<form onSubmit={handleSubmit}>
				<DialogTitle>{t('suggestionDialog.title')}</DialogTitle>
				<DialogContent>
					<DialogContentText variant="body2">
						{t('suggestionDialog.descriptionLabel')}
					</DialogContentText>
					<Box display="flex" flexDirection="column" gap={2}>
						<TextField
							name="description"
							// label={t('suggestionDialog.descriptionLabel')}
							placeholder={t('suggestionDialog.descriptionPlaceholder')}
							fullWidth
							multiline
							rows={5}
							required
							sx={{mt: 1}}
						/>
						<div>
							<FormControlLabel
								name="marketingConsent"
								sx={{ userSelect: 'none' }}
								slotProps={{ typography: { fontSize: '0.9rem' } }}
								control={<Checkbox />}
								label={t('suggestionDialog.marketingConsentCheckboxLabel')}
								onChange={(event, checked) => setIsMarketingConsentChecked(checked)}
							/>
							<FormControlLabel
								name="notifyMeConsent"
								sx={{ userSelect: 'none' }}
								slotProps={{ typography: { fontSize: '0.9rem' } }}
								control={<Checkbox />}
								label={t('suggestionDialog.notifyMeCheckboxLabel')}
								onChange={(event, checked) => setIsNotifyMeConsentChecked(checked)}
							/>
						</div>
						{shouldDisplayEmailField && (
							<TextField
								label={t('addPlanHintDialog.email')}
								name="email"
								fullWidth
								required
							/>
						)}
					</Box>
				</DialogContent>  
				<DialogActions>
					<Button onClick={onClose}>{t('addPlanHintDialog.close')}</Button>
					<Button type="submit" variant="contained" color="primary">{t('addPlanHintDialog.submit')}</Button>
				</DialogActions>
			</form>
		</Dialog>
	</>
	);
};

export default SuggestionDialog;